import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import IndexContentContainer from '../../../index/IndexContentContainer'
import BookReadingSessionResponse from '../../reading/BookReadingSessionResponse'
import CardAddButton from '../../../../common/button/CardAddButton'
import ContentContainer from '../../../../common/styles/ContentContainer'
import RowSpacer from '../../../../common/styles/RowSpacer'
import utils from '../../../../common/utils'
import BookDetailReadingSessionAddModal from './BookDetailReadingSessionAddModal'
import BookDetailReadingSessionEditDeleteModal from './BookDetailReadingSessionEditDeleteModal'
import useBookDetailStore from '../useBookDetailStore'
import BookResponse from '../../BookResponse'
import EmptyResult from '../../../../common/EmptyResult'

const BookDetailReadingSessionCard = () => {
	const { book, readingSessions } = useBookDetailStore()

	const [isAddOpen, setIsAddOpen] = useState<boolean>(false)
	const [isEditOrDeleteOpen, setIsEditOrDeleteOpen] = useState<boolean>(false)
	const [selectedReadingSession, setSelectedReadingSession] = useState<BookReadingSessionResponse | null>(null)

	const openAdd = () => setIsAddOpen(true)
	const closeAdd = () => setIsAddOpen(false)
	const openEditOrDelete = (readingSession: BookReadingSessionResponse) => {
		setIsEditOrDeleteOpen(true)
		setSelectedReadingSession(readingSession)
	}
	const closeEditOrDelete = () => {
		setIsEditOrDeleteOpen(false)
		setSelectedReadingSession(null)
	}

	return (
		<IndexContentContainer>
			<BookDetailReadingSessionAddModal isOpen={isAddOpen} close={closeAdd} />

			{selectedReadingSession != null && <BookDetailReadingSessionEditDeleteModal isOpen={isEditOrDeleteOpen} close={closeEditOrDelete} readingSession={selectedReadingSession} />}

			<CardAddButton onClick={openAdd} />
			{book != null && <CardReadingSessionSummary book={book} readingSessions={readingSessions} />}

			<Container>
					{readingSessions.length > 0 ? (
						<NonEmptyCaseContainer>
							{readingSessions
								.map(readingSession => (
									<CardColumn key={readingSession.id}>
									<ReadingSessionContainer height={100} onClick={() => openEditOrDelete(readingSession)}>
										<ReadingSessionTimeContainer>{readingSession.readTimeInMinutes != null ? `${readingSession.readTimeInMinutes}분` : '-'}</ReadingSessionTimeContainer>

										<ReadingSessionPageContainer>
											<Bold>{readingSession.startPage ?? '-'}</Bold>P - <Bold>{readingSession.endPage ?? '-'}</Bold>P
										</ReadingSessionPageContainer>

										<ReadingSessionDateContainer>{utils.formatTime(readingSession.startTime?.toString() ?? null)}</ReadingSessionDateContainer>

										<RowSpacer size={10} />
									</ReadingSessionContainer>
								</CardColumn>
								))}
						</NonEmptyCaseContainer>
					) : (
						<EmptyCase />
					)}
			</Container>
		</IndexContentContainer>
	)
}

const EmptyCase = () => {
	return <EmptyResult icon="📚" title="기록하신 독서 활동이 없어요" description="상단의 + 버튼으로 직접 추가하거나 이어서 읽기를 눌러서 기록해 보세요" />
}

interface CardReadingSessionSummaryProps {
	book: BookResponse
	readingSessions: BookReadingSessionResponse[]
}

const CardReadingSessionSummary: React.FC<CardReadingSessionSummaryProps> = ({ book, readingSessions }) => {
	const Container = styled.div`
		padding: 0;

		position: absolute;
		top: 20px;
		left: 20px;

		display: flex;
		flex-direction: row;
	`

	const Badge = styled.div.attrs({
		className: 'force-1-line',
	})`
        color: white;
        background-color: var(--badge-bg-color);
        font-weight: bold;
        text-align: center;
        border-radius: 16px;
        padding: 5px 10px;
        width: fit-content;

        margin-left: 0;
        margin-right: 5px;
    `

	const [totalTimeInMinutes, setTotalTimeInMinutes] = useState(0)
	const [estimatedTimeInMinutes, setEstimatedTimeInMinutes] = useState('?')
	useEffect(() => {
		if (readingSessions != undefined) {
			let totalTime = readingSessions.reduce((acc, readingSession) => acc + (readingSession.readTimeInMinutes ?? 0), 0)

			let pagesForTotalTime = readingSessions.filter(readingSession => readingSession.startPage != null && readingSession.endPage != null).reduce((acc, readingSession) => acc + (readingSession.endPage ?? 0) - (readingSession.startPage ?? 0), 0)

			let minutePerPage = totalTime / pagesForTotalTime

			let maxEndPage = Math.max(...readingSessions.map(session => session.endPage ?? 0))
			let remainPages = book.endPage - maxEndPage
			let estimatedRemainTimeInMinutes = Math.round(remainPages * minutePerPage)

			setTotalTimeInMinutes(totalTime)
			setEstimatedTimeInMinutes(Number.isFinite(estimatedRemainTimeInMinutes) && !isNaN(estimatedRemainTimeInMinutes) ? estimatedRemainTimeInMinutes.toString() : '?')
		}
	}, [readingSessions])

	return (
		<Container>
			<Badge>총 {utils.formatMinutes(totalTimeInMinutes)}</Badge>

			{book?.status != 'DONE' && estimatedTimeInMinutes !== '?' && <Badge>{utils.formatMinutes(estimatedTimeInMinutes)} 남음</Badge>}
		</Container>
	)
}


const Container = styled.div`
	min-height: 250px;
	width: 100%;
	padding: 50px 10px 10px 10px;
`

const NonEmptyCaseContainer = styled.div.attrs({
	className: 'row',
})``

interface ReadingSessionContainerProps {
	onClick: (readingSession: BookReadingSessionResponse) => void
}

const CardColumn = styled.div.attrs({
	className: 'col-12 col-sm-6 col-md-4 col-xl-3',
})`
	padding: 4px;
`

const ReadingSessionContainer = styled(ContentContainer).attrs({
	className: 'bg-card-card',
})<ReadingSessionContainerProps>`
	color: var(--text-color);
	border-radius: 16px;
	width: 100%;
`

const ReadingSessionDateContainer = styled.div`
	position: absolute;
	right: 15px;
	bottom: 15px;
`

const ReadingSessionTimeContainer = styled.div`
	font-size: 40px;
`

const ReadingSessionPageContainer = styled.div`
	position: absolute;
	right: 15px;
	bottom: 40px;
`

const Bold = styled.span`
	color: var(--primary-color);
	font-weight: bold;
`

export default BookDetailReadingSessionCard
